import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, DollarSign, MapPin, Bot, ArrowRight, CheckCircle, TrendingDown, Users, Shield, LocateFixed, Loader2 } from 'lucide-react';
import { useGeoState } from '../utils/geoState';

const POPULAR_PROCEDURES = [
  "Knee Replacement", "Hip Replacement", "Cataract Surgery", "Spinal Fusion",
  "Gastric Sleeve", "ACL Reconstruction", "Colonoscopy", "Gallbladder Removal"
];

const STATS = [
  { value: "200+", label: "Verified surgical centers", icon: MapPin },
  { value: "40+", label: "States covered", icon: Shield },
  { value: "68%", label: "Average savings vs. insurance", icon: TrendingDown },
  { value: "40+", label: "Common procedures listed", icon: Users },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Find Your Procedure",
    desc: "Search our curated list of 40+ procedures, use AI-powered search by description, or upload your doctor's referral for instant CPT code identification."
  },
  {
    step: "02",
    title: "Compare Cash vs. Insurance",
    desc: "Enter your deductible and out-of-pocket maximum to see exactly how much you'd pay through insurance vs. a transparent cash rate."
  },
  {
    step: "03",
    title: "Discover Top Providers",
    desc: "Browse verified surgical centers across all 50 states with their publicly-posted cash prices. Filter by state, specialty, and savings."
  },
  {
    step: "04",
    title: "Plan Your Journey",
    desc: "Estimate travel and lodging costs. In many cases, flying to a lower-cost state still saves thousands compared to local insurance rates."
  },
];

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [geoState, setGeoState] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    const dest = q ? `/search-results?q=${encodeURIComponent(q)}` : '/procedures';
    navigate(geoState ? `${dest}&state=${geoState}` : dest);
  };

  const { loading: geoLoading, detect: detectLocation } = useGeoState(
    useCallback((state) => {
      setGeoState(state);
      // Re-run current search with the detected state if a query is already typed
      const q = searchQuery.trim();
      if (q) navigate(`/search-results?q=${encodeURIComponent(q)}&state=${state}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, searchQuery]),
  );

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Hero Section — Midnight Teal with cartographic grid overlay */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" style={{ backgroundColor: '#0D3D4A' }}>
        {/* Cartographic meridian lines texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.05,
            backgroundImage: [
              'repeating-linear-gradient(0deg, transparent, transparent 49px, rgba(255,255,255,1) 49px, rgba(255,255,255,1) 50px)',
              'repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(255,255,255,1) 49px, rgba(255,255,255,1) 50px)',
            ].join(','),
            backgroundSize: '50px 50px',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-20">
          <div className="max-w-3xl animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/80 px-4 py-2 text-xs tracking-widest uppercase font-semibold mb-8">
              <Shield size={12} />
              Transparent Cash-Pay Surgical Pricing
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Surgery shouldn't cost you your savings.
            </h1>
            <p className="text-lg mb-10 leading-relaxed max-w-2xl" style={{ color: 'rgba(248,247,245,0.75)' }}>
              Meridian helps you find the lowest cash prices for surgical procedures at accredited centers across all 50 states — and compare them against your insurance out-of-pocket costs.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-0 max-w-2xl" data-testid="hero-search-form">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8B4B8]" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Describe what you need — 'I need a colonoscopy in Texas under $2,000' or 'knee replacement, can travel 3 hours from Nashville'"
                  className="w-full pl-12 pr-4 py-4 bg-white border-0 focus:outline-none focus:ring-0 text-sm"
                  style={{ color: '#0D3D4A' }}
                  data-testid="hero-search-input"
                />
              </div>
              <button
                type="button"
                onClick={detectLocation}
                disabled={geoLoading}
                title="Detect my location"
                className="flex items-center justify-center gap-1.5 px-4 py-4 text-sm font-medium transition-colors disabled:opacity-60 whitespace-nowrap"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'white' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'}
                data-testid="hero-near-me-btn"
              >
                {geoLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Detecting…</>
                  : <><LocateFixed size={14} /> {geoState ? geoState : 'Near me'}</>
                }
              </button>
              <button
                type="submit"
                className="text-white px-8 py-4 font-medium transition-colors flex items-center gap-2 whitespace-nowrap text-sm"
                style={{ backgroundColor: '#0B3240', border: '1px solid rgba(255,255,255,0.2)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0E4557'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#0B3240'}
                data-testid="hero-search-button"
              >
                Search <ArrowRight size={16} />
              </button>
            </form>

            {/* Popular Procedures */}
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Popular:</span>
              {POPULAR_PROCEDURES.map(p => (
                <button
                  key={p}
                  onClick={() => navigate(`/search-results?q=${encodeURIComponent(p)}`)}
                  className="text-xs text-white px-3 py-1.5 transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.30)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                  data-testid={`popular-procedure-${p.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0D3D4A] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 animate-stagger">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-semibold text-white mb-1" style={{fontFamily: 'Outfit, sans-serif'}} data-testid={`stat-value-${i}`}>{stat.value}</p>
                <p className="text-[#F0EEE9]/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 md:py-32 bg-[#F0EEE9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-4">How Meridian Works</p>            <h2 className="text-3xl sm:text-4xl font-medium text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              Your journey to affordable surgery in 4 steps
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto text-base">
              From finding your procedure to booking your care — Meridian guides you every step of the way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 animate-stagger">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={i} className="bg-white border border-stone-200 p-8 card-hover relative" data-testid={`how-step-${i}`}>
                <span className="text-5xl font-bold text-stone-100 absolute top-4 right-6" style={{fontFamily: 'Outfit, sans-serif'}}>{step.step}</span>
                <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Step {step.step}</p>
                <h3 className="text-lg font-semibold text-stone-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>{step.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-4">AI-Powered Tools</p>
              <h2 className="text-3xl sm:text-4xl font-medium text-stone-900 mb-6 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                Don't know your procedure name? We'll help you find it.
              </h2>
              <p className="text-stone-600 mb-8 leading-relaxed">
                Upload your doctor's referral letter, clinical notes, or even take a photo of your paperwork. Our AI instantly identifies your procedure, the correct CPT code, and connects you with the best-priced providers.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Upload documents or photos of medical paperwork",
                  "AI identifies your procedure and CPT code",
                  "Describe symptoms and get matched to procedures",
                  "Get a patient-friendly explanation of your surgery"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[#0D3D4A] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-stone-600">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/ai-assistant')}
                className="bg-[#0D3D4A] text-white px-8 py-4 text-sm font-medium hover:bg-[#0E4557] transition-colors flex items-center gap-2"
                data-testid="try-ai-assistant-btn"
              >
                Try AI Assistant <ArrowRight size={16} />
              </button>
            </div>
            <div className="bg-[#F0EEE9] border border-stone-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#0D3D4A] flex items-center justify-center">
                  <Bot size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900 text-sm">Meridian AI</p>
                  <p className="text-xs text-stone-500">Powered by Claude Sonnet</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-white border border-stone-200 p-4 text-sm text-stone-700 leading-relaxed">
                  "My doctor says I need a procedure for my L4-L5 disc. I have a $6,000 deductible but haven't met any of it yet."
                </div>
                <div className="bg-[#F0EEE9] border border-[#0D3D4A]/10 p-4 text-sm text-stone-700 leading-relaxed">
                  <strong className="text-[#0D3D4A]">Meridian AI:</strong> Based on your description, you likely need an <strong>L4-L5 Discectomy</strong> (CPT 63030) or possibly a <strong>Spinal Fusion</strong> (CPT 22612). With a $6,000 deductible unmet, your insurance out-of-pocket could reach $6,000–$8,000+. Cash prices at our partner centers start at <strong>$19,800</strong> — which may actually be less than what you'd pay through insurance...
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Showcase */}
      <section className="py-24 bg-[#F0EEE9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-4">Real Savings</p>
            <h2 className="text-3xl sm:text-4xl font-medium text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              See how much you could save
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto">
              Based on publicly-posted cash prices vs. average insurance out-of-pocket costs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Total Knee Replacement", cash: 17550, insurance: 35000, savings: 17450 },
              { name: "Spinal Fusion", cash: 26500, insurance: 65000, savings: 38500 },
              { name: "Gastric Sleeve", cash: 10800, insurance: 28000, savings: 17200 },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-stone-200 p-8 card-hover" data-testid={`savings-card-${i}`}>
                <h3 className="font-semibold text-stone-900 mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>{item.name}</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-stone-500">Insurance OOP Est.</span>
                    <span className="font-semibold text-[#A8B4B8] line-through">${item.insurance.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-stone-700 font-medium">Cash Pay Price</span>
                    <span className="font-bold text-[#0D3D4A] text-lg">${item.cash.toLocaleString()}</span>
                  </div>
                  <div className="h-px bg-stone-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-orange-600">You save</span>
                    <span className="font-bold text-orange-600 text-xl">${item.savings.toLocaleString()}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/procedures')}
                  className="w-full text-center text-sm text-[#0D3D4A] font-medium hover:text-[#0D3D4A] flex items-center justify-center gap-1"
                >
                  Find providers <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0D3D4A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
            Ready to take control of your healthcare costs?
          </h2>
          <p className="text-[#F0EEE9]/70 mb-10 text-lg max-w-2xl mx-auto">
            Join thousands of patients who've discovered they can afford the care they need — without breaking the bank.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/auth?mode=register')}
              className="bg-white text-[#0D3D4A] px-8 py-4 font-semibold text-sm hover:bg-[#F0EEE9] transition-colors"
              data-testid="cta-register-btn"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/procedures')}
              className="border border-[#0D3D4A]/50 text-white px-8 py-4 font-medium text-sm hover:bg-[#0E4557] transition-colors"
              data-testid="cta-browse-btn"
            >
              Browse Procedures
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
